import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ImageIcon, X, Loader2, Newspaper, Package } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { PostRoom } from "@/hooks/usePosts";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CreatePostDialogProps {
  onCreatePost: (imageFile: File | null, caption: string, options?: { roomId?: string | null; sharedArticleId?: string | null; sharedProductId?: string | null }) => Promise<void>;
  rooms: PostRoom[];
  currentRoomId?: string | null;
  isAdmin?: boolean;
}

interface SharedItem {
  type: "article" | "product";
  id: string;
  title: string;
  image?: string | null;
}

export function CreatePostDialog({ onCreatePost, rooms, currentRoomId, isAdmin }: CreatePostDialogProps) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(currentRoomId || null);
  const [sharedItem, setSharedItem] = useState<SharedItem | null>(null);
  const [showSharePicker, setShowSharePicker] = useState<"article" | "product" | null>(null);
  const [articles, setArticles] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedRoomId(currentRoomId || null);
  }, [currentRoomId]);

  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const analyzeImage = async (file: File) => {
    setAiLoading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });

      const { data, error } = await supabase.functions.invoke("ai-chat", {
        body: {
          messages: [
            {
              role: "user",
              content: `Analizza questa immagine. Se contiene dispositivi tech (smartphone, tablet, cuffie, laptop, ecc.), identifica il modello specifico e genera:
1. Tag tecnici suggeriti (es: #iPhone16Pro #AppleWatch)
2. Una breve descrizione per un post social (max 2 righe, in italiano)
Se non ci sono dispositivi tech, suggerisci comunque una breve descrizione del post.
Rispondi in formato:
TAG: #tag1 #tag2
DESC: la descrizione`,
              image: base64,
            },
          ],
          skipMemory: true,
        },
      });

      if (data && !error) {
        // Parse response
        let responseText = "";
        if (typeof data === "string") {
          const lines = data.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ") && line.slice(6).trim() !== "[DONE]") {
              try {
                const parsed = JSON.parse(line.slice(6));
                const content = parsed.choices?.[0]?.delta?.content;
                if (content) responseText += content;
              } catch {}
            }
          }
        } else if (data?.choices?.[0]?.message?.content) {
          responseText = data.choices[0].message.content;
        }

        if (responseText) {
          setAiSuggestion(responseText);
          // Auto-fill caption with description
          const descMatch = responseText.match(/DESC:\s*(.+)/i);
          const tagMatch = responseText.match(/TAG:\s*(.+)/i);
          if (descMatch || tagMatch) {
            const desc = descMatch?.[1]?.trim() || "";
            const tags = tagMatch?.[1]?.trim() || "";
            setCaption(`${desc}${tags ? "\n\n" + tags : ""}`);
          }
        }
      }
    } catch {
      // Silently fail - AI suggestion is optional
    } finally {
      setAiLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine valida"); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("L'immagine deve essere inferiore a 50MB"); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    analyzeImage(file);
  };

  const loadArticles = async () => {
    const { data } = await supabase.from("articles").select("id, title, description, image_key").eq("is_published", true).order("created_at", { ascending: false }).limit(50);
    setArticles(data || []);
    setShowSharePicker("article");
  };

  const loadProducts = async () => {
    const { data } = await supabase.from("products").select("id, name, description, cover_image, base_price").eq("is_active", true).order("created_at", { ascending: false }).limit(50);
    setProducts(data || []);
    setShowSharePicker("product");
  };

  const moderateContent = async (text: string, imageFile: File | null): Promise<{ approved: boolean; reason?: string }> => {
    try {
      let imageBase64: string | undefined;
      if (imageFile) {
        const reader = new FileReader();
        imageBase64 = await new Promise<string>((resolve) => {
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(imageFile);
        });
      }

      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/moderate-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ text: text || undefined, imageBase64 }),
      });

      if (!resp.ok) return { approved: true };
      return await resp.json();
    } catch {
      return { approved: true }; // Fail open
    }
  };

  const handleSubmit = async () => {
    if (!imageFile && !sharedItem) { toast.error("Aggiungi un'immagine o condividi un contenuto"); return; }

    // Local profanity guard — catches insults even if written with leet,
    // accents, repeated letters or separators. Runs before AI moderation.
    const { checkProfanity } = await import("@/lib/profanityFilter");
    const local = checkProfanity(caption);
    if (!local.clean) {
      toast.error("Il post contiene linguaggio offensivo e non può essere pubblicato.", { duration: 5000 });
      return;
    }

    setLoading(true);
    try {
      // Content moderation check
      const modResult = await moderateContent(caption, imageFile);
      if (!modResult.approved) {
        toast.error(modResult.reason || "Il contenuto viola le linee guida di Tecly e non può essere pubblicato.", { duration: 5000 });
        setLoading(false);
        return;
      }


      await onCreatePost(imageFile, caption, {
        roomId: selectedRoomId,
        sharedArticleId: sharedItem?.type === "article" ? sharedItem.id : null,
        sharedProductId: sharedItem?.type === "product" ? sharedItem.id : null,
      });
      toast.success("Post pubblicato!");
      reset();
      setOpen(false);
    } catch (err: any) {
      if (err.message === "USERNAME_REQUIRED") {
        toast.error("Devi impostare un username nel profilo prima di pubblicare");
      } else {
        toast.error("Errore durante la pubblicazione");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCaption("");
    setImageFile(null);
    setImagePreview(null);
    setSharedItem(null);
    setShowSharePicker(null);
    setSelectedRoomId(currentRoomId || null);
    setAiSuggestion(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="icon" className="rounded-full w-12 h-12 shadow-lg bg-primary hover:bg-primary/90">
          <Plus className="w-6 h-6" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-full h-full max-h-screen sm:h-auto sm:max-h-[95vh] rounded-none sm:rounded-2xl p-0 gap-0 overflow-hidden" style={{ width: '100vw', maxWidth: '100vw' }}>
        <DialogHeader className="px-4 py-3 border-b border-border relative">
          <DialogTitle>Nuovo Post</DialogTitle>
          <button
            onClick={() => { setOpen(false); reset(); }}
            aria-label="Chiudi"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/60 active:scale-90 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>
        
        {showSharePicker ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="px-4 py-2 flex items-center justify-between border-b border-border">
              <p className="text-sm font-medium">
                {showSharePicker === "article" ? "Seleziona una News" : "Seleziona un Prodotto"}
              </p>
              <button onClick={() => setShowSharePicker(null)} className="text-sm text-primary">Indietro</button>
            </div>
            <ScrollArea className="flex-1 max-h-[70vh]">
              <div className="p-4 space-y-2">
                {(showSharePicker === "article" ? articles : products).map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSharedItem({
                        type: showSharePicker,
                        id: item.id,
                        title: showSharePicker === "article" ? item.title : item.name,
                        image: showSharePicker === "article" ? item.image_key : item.cover_image,
                      });
                      setShowSharePicker(null);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted/50 transition-colors text-left"
                  >
                    {(showSharePicker === "article" ? item.image_key : item.cover_image) && (
                      <img
                        src={showSharePicker === "article" ? item.image_key : item.cover_image}
                        alt=""
                        className="w-12 h-12 rounded-lg object-cover bg-muted flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {showSharePicker === "article" ? item.title : item.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {showSharePicker === "article" ? item.description : (item.base_price ? `€${item.base_price}` : item.description)}
                      </p>
                    </div>
                  </button>
                ))}
                {(showSharePicker === "article" ? articles : products).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">Nessun contenuto disponibile</p>
                )}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Room selector */}
            {rooms.length > 0 && (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                <button
                  onClick={() => setSelectedRoomId(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                    !selectedRoomId ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border hover:border-primary/50"
                  }`}
                >
                  Generale
                </button>
                {rooms.map(room => (
                  <button
                    key={room.id}
                    onClick={() => setSelectedRoomId(room.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                      selectedRoomId === room.id ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border hover:border-primary/50"
                    }`}
                  >
                    {room.name}
                  </button>
                ))}
              </div>
            )}

            {/* Shared item preview */}
            {sharedItem && (
              <div className="relative rounded-xl border border-primary/30 bg-primary/5 p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase font-semibold text-primary tracking-wider">
                    {sharedItem.type === "article" ? "News" : "Prodotto"}
                  </p>
                  <p className="text-sm font-medium text-foreground truncate">{sharedItem.title}</p>
                </div>
                <button onClick={() => setSharedItem(null)} className="p-1 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
              </div>
            )}

            {/* Image */}
            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden bg-muted">
                <img src={imagePreview} alt="Preview" className="w-full max-h-[60vh] object-contain" />
                <button onClick={() => { setImageFile(null); setImagePreview(null); setAiSuggestion(null); }} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors">
                  <X className="w-4 h-4" />
                </button>
                {aiLoading && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                    <span className="text-xs text-white font-medium">Analisi AI...</span>
                  </div>
                )}
              </div>
            ) : !sharedItem ? (
              <button onClick={() => fileInputRef.current?.click()} className="w-full h-48 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-3 transition-colors bg-muted/50">
                <ImageIcon className="w-10 h-10 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tocca per aggiungere una foto</span>
              </button>
            ) : null}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

            {/* Share buttons (admin can share news/products, users only photo) */}
            {isAdmin && !sharedItem && (
              <div className="flex gap-2">
                <button onClick={loadArticles} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border hover:bg-muted/50 transition-colors text-sm text-muted-foreground">
                  <Newspaper className="w-4 h-4" /> Condividi News
                </button>
                <button onClick={loadProducts} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border hover:bg-muted/50 transition-colors text-sm text-muted-foreground">
                  <Package className="w-4 h-4" /> Condividi Prodotto
                </button>
              </div>
            )}
            
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Scrivi una didascalia..." className="resize-none" maxLength={2200} rows={3} />
            
            <Button onClick={handleSubmit} disabled={loading || (!imageFile && !sharedItem)} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Pubblica
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
